import {gql} from '@apollo/client';
import {Empty, Image, Skeleton} from 'antd';
import {useParams} from 'react-router-dom';
import {Address, SocialShare} from 'src/components';
import {usePrerenderMetaTags, useQueryParams, useQuery} from 'src/hooks';
import {itoh} from 'src/utils';
import {QueryArtistById} from './__generated__/QueryArtistById';
import {QueryArtistByImageUri} from './__generated__/QueryArtistByImageUri';
import {_QueryArtistFragment} from './__generated__/_QueryArtistFragment';
import styles from './Artist.module.scss';

const _QUERY_ARTIST_FRAGMENT = gql`
  fragment _QueryArtistFragment on Artist {
    name
    description
    imageUri
    uri # Needed for metadata augmentation
    owner {
      id
    }
  }
`;

const QUERY_BY_ID = gql`
  query QueryArtistById($id: ID!) {
    artist(subgraphError: allow, id: $id) {
      ..._QueryArtistFragment
    }
  }
  ${_QUERY_ARTIST_FRAGMENT}
`;

const QUERY_BY_IMAGE_URI = gql`
  query QueryArtistByImageUri($imageUri: String!) {
    artists(subgraphError: allow, where: {imageUri: $imageUri}) {
      ..._QueryArtistFragment
    }
  }
  ${_QUERY_ARTIST_FRAGMENT}
`;

function generateMetaTags(artist: _QueryArtistFragment) {
  return {
    'og:title': artist.name,
    'og:description': artist.description,
    'og:image': itoh(artist.imageUri),
  };
}

/**
 * Page for /artists/:id and /artist?imageUri=ipfs://Qm....
 */
export default function Artist() {
  const {id} = useParams<{id: string}>();
  const {imageUri} = useQueryParams();

  const isQueryByImageUri = !!imageUri;
  let query, variables;
  if (isQueryByImageUri) {
    query = QUERY_BY_IMAGE_URI;
    variables = {imageUri};
  } else {
    query = QUERY_BY_ID;
    variables = {id};
  }

  const {data, loading} = useQuery<QueryArtistById | QueryArtistByImageUri>(query, {variables});

  let artist: any;
  if (isQueryByImageUri) {
    artist = (data as QueryArtistByImageUri)?.artists[0];
  } else {
    artist = (data as QueryArtistById)?.artist;
  }

  const metaTags = usePrerenderMetaTags<_QueryArtistFragment>(artist, generateMetaTags);

  return (
    <>
      <Skeleton loading={loading}>
        <div className={styles.container}>
          {artist ? (
            <>
              <div className={styles.frame}>
                <Image
                  className={styles.mat}
                  src={itoh(artist.imageUri)}
                  fallback={process.env.PUBLIC_URL + '/missing.jpg'}
                  alt='art'
                />
              </div>
              <div className={styles.caption}>
                <div className={styles.title}>{artist.name}</div>
                <p className={styles.info}>Artist</p>
                <p className={styles.info}>{artist.description}</p>
                <div className={styles.info}>
                  Owner: <Address address={artist.owner.id} size='small' isLinked />
                </div>
              </div>

              <SocialShare text='Check out this artist in the Lisa Mona NFT collection!' />
            </>
          ) : (
            <Empty description='Artist not found' />
          )}
        </div>
      </Skeleton>
      {metaTags}
    </>
  );
}
